import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { saveSession } from "@/lib/api";
import { fetchMyTeam, fetchAdminUsers, fetchAdminEmailTypes, fetchCrmGroups, isAdminUser, adminImpersonate, adminCreateOrder, fetchPendingCarts,
  deletePendingCarts, deleteContacts, deleteAdminUsers, promoShare } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import BookingsList from "@/components/BookingsList";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import EmailSendModal from "@/components/EmailSendModal";
import SmsSendModal from "@/components/SmsSendModal";
import CouponUpgradeModal from "@/components/CouponUpgradeModal";
import PointsAwardModal from "@/components/PointsAwardModal";
import AdminUserEditSheet from "@/components/AdminUserEditSheet";
import AddContactDialog from "@/components/AddContactDialog";
import ImportContactsDialog from "@/components/ImportContactsDialog";
import OrderDetailSheet from "@/components/OrderDetailSheet";
import DraftOrderSheet from "@/components/DraftOrderSheet";
import ContactDetailSheet from "@/components/ContactDetailSheet";
import { DealerContactFilters, applyDealerContactFilters, EMPTY_DEALER_FILTERS } from "@/components/DealerContactFilters";
import {
  Search,
  Plus,
  Upload,
  Ticket,
  Gift,
  Mail,
  MessageSquare,
  Trash2,
  Loader2,
  Users as UsersIcon,
  ArrowUpDown,
  Filter,
  ShoppingBag,
  ShoppingCart,
  DollarSign,
  UserCog,
  Pencil,
  Clock,
  UserPlus,
  MailOpen,
  ShieldCheck,
  CalendarCheck,
  SlidersHorizontal,
  LogIn,
  X as XIcon,
} from "lucide-react";

const ROLE_COLORS = {
  customer: "bg-zinc-100 text-zinc-700",
  spa: "bg-violet-100 text-violet-700",
  ambassador: "bg-violet-100 text-violet-700",
  professional: "bg-emerald-100 text-emerald-700",
  pro: "bg-emerald-100 text-emerald-700",
  administrator: "bg-amber-100 text-amber-700",
  admin: "bg-amber-100 text-amber-700",
  staff: "bg-amber-100 text-amber-700",
};

function roleClass(role) {
  const k = (role || "").toLowerCase();
  return ROLE_COLORS[k] || "bg-zinc-100 text-zinc-700";
}

const ROLE_FILTERS = [
  { value: "all", label: "All roles" },
  { value: "customer", label: "Customer" },
  { value: "ambassador", label: "Ambassador" },
  { value: "spa", label: "Spa" },
  { value: "professional", label: "Professional" },
  { value: "administrator", label: "Administrator" },
];

const SORT_OPTIONS = [
  { value: "recent_added", label: "Recently added" },
  { value: "recent_orders", label: "Recent orders" },
  { value: "recent_login", label: "Recently logged in" },
  { value: "gross_revenue", label: "Gross revenue" },
  { value: "num_orders", label: "# of orders" },
];

function FilterChip({ label, onRemove, testId }) {
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full bg-violet-100 text-violet-700 pl-2.5 pr-1 py-1 text-xs font-medium"
      data-testid={testId}
    >
      {label}
      <button
        type="button"
        onClick={onRemove}
        className="rounded-full hover:bg-violet-200 p-0.5"
        aria-label={`Remove ${label}`}
      >
        <XIcon size={12} />
      </button>
    </span>
  );
}

const PAGE_SIZE = 100;

export default function Contacts() {
  const { user } = useAuth();
  const isAdmin = isAdminUser(user);
  return isAdmin ? <AdminCrmDirectory user={user} /> : <DealerContacts user={user} />;
}

/* ============================================================== */
/* Admin: Full CRM Directory                                      */
/* ============================================================== */
function AdminCrmDirectory({ user }) {
  const navigate = useNavigate();
  const { adoptUser } = useAuth();
  const callerRecId = user?.user_record_id || user?.record_id;
  const [query, setQuery] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [role, setRole] = useState("all");
  // CRM "Groups" = saved Airtable views (favorites). Independent of role.
  const [group, setGroup] = useState("all");
  const [groupOptions, setGroupOptions] = useState([]);
  // Group sub-filters (only shown when a group is selected).
  const [neverOrdered, setNeverOrdered] = useState(false);
  const [lastOrderFrom, setLastOrderFrom] = useState("");
  const [lastOrderTo, setLastOrderTo] = useState("");
  const [loginWithin, setLoginWithin] = useState(""); // "", "7", "30", "90"
  const [notOrderedSince, setNotOrderedSince] = useState(""); // win-back: last order before this date
  const [orderStatus, setOrderStatus] = useState(""); // "", "processing", "pending", "completed", "cancelled"
  const [showFilters, setShowFilters] = useState(false);
  const [approvedFilter, setApprovedFilter] = useState("all");
  const [activeFlags, setActiveFlags] = useState(() => new Set());
  const flagsParam = useMemo(() => Array.from(activeFlags).join(","), [activeFlags]);
  const toggleFlag = useCallback((key) => {
    setActiveFlags((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  }, []);
  const [sort, setSort] = useState("recent_orders");
  // Targeted email-campaign filter: pick a campaign + Opened/Unopened.
  const [emailType, setEmailType] = useState("");
  const [emailOpened, setEmailOpened] = useState("Y"); // "Y" opened | "N" unopened
  const [emailTypeOptions, setEmailTypeOptions] = useState([]);
  // When "Recently Active" is on, order by most-recent login so the list
  // matches Airtable's login_time order (otherwise it stays on the order-date sort).
  const effectiveSort = useMemo(
    () => (activeFlags.has("recently_active") ? "recent_login" : sort),
    [activeFlags, sort]
  );
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [editId, setEditId] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [smsModalOpen, setSmsModalOpen] = useState(false);
  const [couponModalOpen, setCouponModalOpen] = useState(false);
  const [pointsModalOpen, setPointsModalOpen] = useState(false);
  // Draft order sheet for the "+ Order" admin flow.  No Airtable record is
  // created until the admin clicks Save inside the draft sheet.
  const [draftCustomer, setDraftCustomer] = useState(null);
  // Pending-cart filter: when ON, only show users with rows in the Cart
  // table (abandoned carts).  The map is also passed down into the
  // AdminUserEditSheet so it can render the per-user cart items.
  const [pendingCartFilter, setPendingCartFilter] = useState(false);
  const [pendingCartByUser, setPendingCartByUser] = useState({});
  const [pendingCartIds, setPendingCartIds] = useState(new Set());
  // Authoritative user rows from the "Pendng Cart" Airtable view — used
  // directly when the chip is ON so we don't have to intersect with the
  // paginated directory (which may not have loaded all 48).
  const [pendingCartUsers, setPendingCartUsers] = useState([]);
  const reqIdRef = useRef(0);

  // Load the pending-cart map on mount AND every time the user
  // activates the chip — guarantees fresh data even after a long
  // idle session (backend @ttl_cache(60) gives a 1-minute floor).
  const loadPendingCarts = useCallback(async () => {
    if (!callerRecId) return;
    try {
      const res = await fetchPendingCarts();
      setPendingCartByUser(res?.by_user || {});
      setPendingCartIds(new Set(res?.user_record_ids || []));
      setPendingCartUsers(res?.users || []);
    } catch {
      /* non-fatal */
    }
  }, [callerRecId]);

  useEffect(() => {
    loadPendingCarts();
  }, [loadPendingCarts]);

  // Load distinct email-campaign names once (sourced from cached rollups).
  useEffect(() => {
    if (!callerRecId) return;
    fetchAdminEmailTypes(callerRecId)
      .then((list) => setEmailTypeOptions(list || []))
      .catch(() => {});
  }, [callerRecId]);

  // Load CRM group options (saved Airtable views / favorites) once.
  useEffect(() => {
    if (!callerRecId) return;
    fetchCrmGroups(callerRecId)
      .then((list) => setGroupOptions(list || []))
      .catch(() => {});
  }, [callerRecId]);

  useEffect(() => {
    if (pendingCartFilter) loadPendingCarts();
  }, [pendingCartFilter, loadPendingCarts]);

  function handleAddOrder(targetUser) {
    if (!callerRecId || !targetUser?.id) return;
    setDraftCustomer(targetUser);
  }

  // Switch User (admin impersonation) — signs the admin out of their own
  // Impersonation: admin signs into another user's session. We stash the
  // admin's CURRENT session (whichever storage it's in) before swapping, then
  // call `adoptUser()` so React state updates in-place — no hard reload is
  // needed and the auth context never sees a `null` user mid-transition.
  async function handleImpersonate(targetUser) {
    if (!callerRecId || !targetUser?.id) return;
    const ok = window.confirm(
      `Sign out and sign in as ${targetUser.full_name || targetUser.email || "this user"}?`
    );
    if (!ok) return;
    try {
      const res = await adminImpersonate({
        callerUserRecordId: callerRecId,
        targetUserRecordId: targetUser.id,
      });
      if (!res?.user) throw new Error("Empty session in response");

      // Stash the admin's CURRENT serialized session — checking both storages
      // so we don't lose it when the admin signed in without "Remember me".
      try {
        const current =
          localStorage.getItem("postcue.session") ||
          sessionStorage.getItem("postcue.session");
        if (current) {
          localStorage.setItem("postcue.impersonator", current);
        }
      } catch {
        /* quota — non-fatal */
      }

      // Persist + update React state in one call. `saveSession` writes to
      // localStorage (default), which is what `ImpersonationBanner` reads.
      saveSession(res.user);
      adoptUser(res.user);
      toast.success(`Signed in as ${res.user.first_name || "user"}`);
      navigate("/app/home", { replace: true });
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || e.message || "Could not switch user"
      );
    }
  }

  // Debounce search input
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(query.trim()), 300);
    return () => clearTimeout(t);
  }, [query]);

  // Fetch (reset) whenever filters/sort/search change
  const loadDirectory = useCallback(async () => {
    if (!callerRecId) return;
    const reqId = ++reqIdRef.current;
    setLoading(true);
    setError(null);
    try {
      const res = await fetchAdminUsers({
        callerUserRecordId: callerRecId,
        q: debouncedQ,
        role,
        group,
        neverOrdered,
        lastOrderFrom,
        lastOrderTo,
        loginWithin,
        notOrderedSince,
        orderStatus,
        approved: approvedFilter,
        flags: flagsParam,
        emailType,
        emailOpened,
        sort: effectiveSort,
        limit: PAGE_SIZE,
        offset: 0,
      });
      if (reqIdRef.current !== reqId) return;
      setUsers(res.users || []);
      setTotal(res.total || 0);
      setHasMore(!!res.has_more);
    } catch (e) {
      if (reqIdRef.current !== reqId) return;
      setError(e?.response?.data?.detail || e.message || "Could not load directory");
    } finally {
      if (reqIdRef.current === reqId) setLoading(false);
    }
  }, [callerRecId, debouncedQ, role, group, neverOrdered, lastOrderFrom, lastOrderTo, loginWithin, notOrderedSince, orderStatus, approvedFilter, effectiveSort, flagsParam, emailType, emailOpened]);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        if (!active) return;
        await loadDirectory();
      } catch {
        /* swallow */
      }
    })();
    return () => {
      active = false;
    };
  }, [loadDirectory]);

  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const res = await fetchAdminUsers({
        callerUserRecordId: callerRecId,
        q: debouncedQ,
        role,
        group,
        neverOrdered,
        lastOrderFrom,
        lastOrderTo,
        loginWithin,
        notOrderedSince,
        orderStatus,
        approved: approvedFilter,
        flags: flagsParam,
        emailType,
        emailOpened,
        sort: effectiveSort,
        limit: PAGE_SIZE,
        offset: users.length,
      });
      setUsers((u) => [...u, ...(res.users || [])]);
      setHasMore(!!res.has_more);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Load failed");
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore, hasMore, callerRecId, debouncedQ, role, group, neverOrdered, lastOrderFrom, lastOrderTo, loginWithin, notOrderedSince, orderStatus, approvedFilter, effectiveSort, flagsParam, emailType, emailOpened, users.length]);

  // Apply the pending-cart toggle: when ON, render the authoritative
  // user list returned by the backend (48 users from the Airtable
  // "Pendng Cart" view).  Role + search filters still apply on top so
  // "Customer + Pending cart" only shows customers with pending carts.
  const visibleUsers = useMemo(() => {
    if (!pendingCartFilter) return users;
    const ql = (debouncedQ || "").trim().toLowerCase();
    return pendingCartUsers.filter((u) => {
      if (role && role !== "all") {
        if ((u.role_name || "").trim().toLowerCase() !== role.toLowerCase()) {
          return false;
        }
      }
      if (ql) {
        const hay = `${u.full_name || ""} ${u.email || ""} ${u.phone || ""} ${u.user_name || ""} ${u.company_name || ""}`.toLowerCase();
        if (!hay.includes(ql)) return false;
      }
      return true;
    });
  }, [users, pendingCartFilter, pendingCartUsers, role, debouncedQ]);
  const allVisibleSelected =
    visibleUsers.length > 0 && visibleUsers.every((u) => selected.has(u.id));
  const selectedCount = selected.size;
  const selectedUsers = useMemo(
    () => visibleUsers.filter((u) => selected.has(u.id)),
    [visibleUsers, selected]
  );
  const toggleOne = useCallback((id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);
  const toggleAllVisible = useCallback(() => {
    setSelected((prev) => {
      const next = new Set(prev);
      const allOn = visibleUsers.every((u) => next.has(u.id));
      if (allOn) visibleUsers.forEach((u) => next.delete(u.id));
      else visibleUsers.forEach((u) => next.add(u.id));
      return next;
    });
  }, [visibleUsers]);
  const clearSelection = useCallback(() => setSelected(new Set()), []);
  const [deletingCarts, setDeletingCarts] = useState(false);
  async function deleteSelectedCarts() {
    if (deletingCarts) return;
    const ids = selectedUsers.map((u) => u.id);
    const n = ids.length;
    if (!window.confirm(`Delete the pending cart${n === 1 ? "" : "s"} of ${n} contact${n === 1 ? "" : "s"}? Contacts stay — only their cart items are removed.`)) return;
    setDeletingCarts(true);
    try {
      const r = await deletePendingCarts(callerRecId, ids);
      toast.success(`${r.carts_deleted} cart item${r.carts_deleted === 1 ? "" : "s"} removed for ${r.users} contact${r.users === 1 ? "" : "s"}`);
      clearSelection();
      await loadPendingCarts();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete carts");
    } finally {
      setDeletingCarts(false);
    }
  }

  const [deletingUsers, setDeletingUsers] = useState(false);
  async function deleteSelectedUsers() {
    if (deletingUsers || selectedUsers.length === 0) return;
    const withOrders = selectedUsers.filter((u) => Number(u.num_orders || 0) > 0);
    const n = selectedUsers.length;
    const names = selectedUsers.slice(0, 3).map((u) => u.name || u.email).join(", ") + (n > 3 ? ` +${n - 3} more` : "");
    if (!window.confirm(`Permanently delete ${n} contact${n === 1 ? "" : "s"}?\n${names}\n\nThis removes their account, cart and dealer links. It cannot be undone.`)) return;
    let force = false;
    if (withOrders.length) {
      force = window.confirm(`${withOrders.length} of them ${withOrders.length === 1 ? "has" : "have"} orders on file. Delete those too? (Orders/invoices stay in the books, but lose the customer link.)\n\nOK = delete all · Cancel = keep the ones with orders`);
    }
    setDeletingUsers(true);
    try {
      const r = await deleteAdminUsers(callerRecId, selectedUsers.map((u) => u.id), force);
      const gone = new Set(r.deleted_ids || []);
      setUsers((list) => list.filter((u) => !gone.has(u.id)));
      clearSelection();
      toast.success(`${r.deleted} contact${r.deleted === 1 ? "" : "s"} deleted`, {
        description: r.skipped?.length ? `${r.skipped.length} kept (${[...new Set(r.skipped.map((s) => s.reason))].join("; ")})` : undefined,
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete contacts");
    } finally {
      setDeletingUsers(false);
    }
  }

  const bulkAction = (label) => () =>
    toast(`${label} — coming next`, {
      description: `${selectedCount} user${selectedCount === 1 ? "" : "s"} selected`,
    });

  return (
    <div className="px-4 pt-4" data-testid="crm-directory">
      <div className="flex items-center justify-between mb-3">
        <h1 className="text-2xl font-semibold tracking-tight text-zinc-900">
          CRM
        </h1>
        <div className="flex items-center gap-3">
          <div className="text-xs text-zinc-500" data-testid="crm-total">
            {total.toLocaleString()} users
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-3">
        <Search
          size={18}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400"
        />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search name, email, phone, username"
          className="pl-10 h-11 rounded-xl border-zinc-200 bg-white focus-visible:ring-violet-500"
          data-testid="crm-search-input"
        />
      </div>

      {/* Segment (Role OR Group) + sort row */}
      <div className="grid grid-cols-2 gap-2 mb-3">
        <Select
          value={group !== "all" ? `group:${group}` : role !== "all" ? `role:${role}` : "all"}
          onValueChange={(v) => {
            // Segment is mutually exclusive: choosing a role clears the group and
            // vice-versa (no duplicate "which people" filters).
            if (v === "all") {
              setRole("all"); setGroup("all");
            } else if (v.startsWith("role:")) {
              setRole(v.slice(5)); setGroup("all");
            } else if (v.startsWith("group:")) {
              setGroup(v.slice(6)); setRole("all"); setPendingCartFilter(false);
            }
          }}
        >
          <SelectTrigger className="h-11 rounded-xl bg-white" data-testid="crm-segment-filter">
            <div className="flex items-center gap-1.5 min-w-0">
              <Filter size={14} className="text-zinc-400 shrink-0" />
              <SelectValue />
            </div>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Everyone</SelectItem>
            <div className="px-2 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wide text-zinc-400">Roles</div>
            {ROLE_FILTERS.filter((r) => r.value !== "all").map((r) => (
              <SelectItem key={`role:${r.value}`} value={`role:${r.value}`} data-testid={`crm-seg-role-${r.value}`}>
                {r.label}
              </SelectItem>
            ))}
            {groupOptions.length > 0 && (
              <>
                <div className="px-2 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wide text-zinc-400">Groups</div>
                {groupOptions.map((g) => (
                  <SelectItem key={`group:${g}`} value={`group:${g}`} data-testid={`crm-seg-group-${g}`}>
                    {g}
                  </SelectItem>
                ))}
              </>
            )}
          </SelectContent>
        </Select>
        <Select
          value={
            pendingCartFilter
              ? "pending_cart"
              : approvedFilter === "approved"
                ? "approved"
                : sort
          }
          onValueChange={(v) => {
            if (v === "pending_cart") {
              setPendingCartFilter(true);
              setApprovedFilter("all");
            } else if (v === "approved") {
              setApprovedFilter("approved");
              setPendingCartFilter(false);
            } else {
              setPendingCartFilter(false);
              setApprovedFilter("all");
              setSort(v);
            }
          }}
        >
          <SelectTrigger
            className="h-11 rounded-xl bg-white"
            data-testid="crm-sort-select"
          >
            <div className="flex items-center gap-1.5 min-w-0">
              <ArrowUpDown size={14} className="text-zinc-400 shrink-0" />
              <SelectValue />
            </div>
          </SelectTrigger>
          <SelectContent>
            <SelectItem
              value="pending_cart"
              className="font-semibold text-amber-700"
            >
              <span className="inline-flex items-center gap-1.5">
                <ShoppingCart size={12} />
                Pending cart
                {pendingCartIds.size > 0 && (
                  <span className="ml-1 text-[10px] bg-amber-100 text-amber-700 rounded-full px-1.5 py-0.5 leading-none">
                    {pendingCartIds.size}
                  </span>
                )}
              </span>
            </SelectItem>
            <SelectItem
              value="approved"
              className="font-semibold text-emerald-700"
              data-testid="crm-sort-approved"
            >
              <span className="inline-flex items-center gap-1.5">
                <ShieldCheck size={12} />
                Approved
              </span>
            </SelectItem>
            {SORT_OPTIONS.map((s) => (
              <SelectItem key={s.value} value={s.value}>
                {s.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Filters — refinements that work for ANY segment (role, group, everyone) */}
      <div className="mb-3">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setShowFilters((v) => !v)}
            className={`inline-flex items-center gap-1.5 rounded-xl border px-3 h-9 text-sm font-medium transition-colors ${
              showFilters ? "bg-violet-600 border-violet-600 text-white" : "bg-white border-zinc-200 text-zinc-700 hover:border-zinc-300"
            }`}
            data-testid="crm-filters-toggle"
          >
            <SlidersHorizontal size={14} /> Filters
            {(neverOrdered || lastOrderFrom || lastOrderTo || loginWithin || notOrderedSince || orderStatus) && (
              <span className="ml-1 text-[10px] rounded-full bg-white/25 px-1.5 py-0.5 leading-none">
                {[neverOrdered, lastOrderFrom || lastOrderTo, loginWithin, notOrderedSince, orderStatus].filter(Boolean).length}
              </span>
            )}
          </button>
          {/* Active filter chips (always visible, removable) */}
          {neverOrdered && (
            <FilterChip label="Never ordered" onRemove={() => setNeverOrdered(false)} testId="crm-activechip-never-ordered" />
          )}
          {(lastOrderFrom || lastOrderTo) && (
            <FilterChip
              label={`Ordered ${lastOrderFrom || "…"} → ${lastOrderTo || "…"}`}
              onRemove={() => { setLastOrderFrom(""); setLastOrderTo(""); }}
              testId="crm-activechip-ordered-range"
            />
          )}
          {loginWithin && (
            <FilterChip label={`Logged in ≤ ${loginWithin}d`} onRemove={() => setLoginWithin("")} testId="crm-activechip-login" />
          )}
          {notOrderedSince && (
            <FilterChip label={`No order since ${notOrderedSince}`} onRemove={() => setNotOrderedSince("")} testId="crm-activechip-not-since" />
          )}
          {orderStatus && (
            <FilterChip label={`Order status: ${orderStatus}`} onRemove={() => setOrderStatus("")} testId="crm-activechip-order-status" />
          )}
        </div>

        {showFilters && (
          <div className="mt-2 rounded-xl border border-zinc-200 bg-zinc-50/70 p-3 space-y-3" data-testid="crm-filters-panel">
            {/* Never ordered */}
            <button
              type="button"
              onClick={() => setNeverOrdered((v) => { const n = !v; if (n) { setLastOrderFrom(""); setLastOrderTo(""); } return n; })}
              aria-pressed={neverOrdered}
              className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                neverOrdered ? "bg-rose-600 border-rose-600 text-white" : "bg-white border-zinc-200 text-zinc-700 hover:border-zinc-300"
              }`}
              data-testid="crm-subfilter-never-ordered"
            >
              <ShoppingBag size={13} /> Never ordered
            </button>

            {/* Ordered between */}
            <div className={`flex items-center gap-2 ${neverOrdered ? "opacity-40 pointer-events-none" : ""}`}>
              <span className="text-[11px] text-zinc-500 shrink-0 w-24">Ordered between</span>
              <Input type="date" value={lastOrderFrom} onChange={(e) => setLastOrderFrom(e.target.value)} className="h-9 rounded-lg text-xs" data-testid="crm-subfilter-from" />
              <span className="text-[11px] text-zinc-400">to</span>
              <Input type="date" value={lastOrderTo} onChange={(e) => setLastOrderTo(e.target.value)} className="h-9 rounded-lg text-xs" data-testid="crm-subfilter-to" />
            </div>

            {/* Hasn't ordered since (win-back) */}
            <div className={`flex items-center gap-2 ${neverOrdered ? "opacity-40 pointer-events-none" : ""}`}>
              <span className="text-[11px] text-zinc-500 shrink-0 w-24">No order since</span>
              <Input type="date" value={notOrderedSince} onChange={(e) => setNotOrderedSince(e.target.value)} className="h-9 rounded-lg text-xs" data-testid="crm-subfilter-not-since" />
              <span className="text-[10px] text-zinc-400 leading-tight">customers whose last order was before this date</span>
            </div>

            {/* Order status */}
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[11px] text-zinc-500 shrink-0 w-24">Order status</span>
              {[
                { v: "", l: "Any" },
                { v: "pending", l: "Pending" },
                { v: "processing", l: "Processing" },
                { v: "completed", l: "Completed" },
                { v: "cancelled", l: "Cancelled" },
              ].map((o) => (
                <button
                  key={o.v || "any"}
                  type="button"
                  onClick={() => setOrderStatus(o.v)}
                  className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                    orderStatus === o.v ? "bg-violet-600 border-violet-600 text-white" : "bg-white border-zinc-200 text-zinc-700 hover:border-zinc-300"
                  }`}
                  data-testid={`crm-order-status-${o.v || "any"}`}
                >
                  {o.l}
                </button>
              ))}
            </div>

            {/* Recently logged in */}
            <div className="flex items-center gap-2">
              <span className="text-[11px] text-zinc-500 shrink-0 w-24 inline-flex items-center gap-1"><LogIn size={12} /> Logged in</span>
              {[
                { v: "", l: "Any" },
                { v: "7", l: "≤ 7d" },
                { v: "30", l: "≤ 30d" },
                { v: "90", l: "≤ 90d" },
              ].map((o) => (
                <button
                  key={o.v || "any"}
                  type="button"
                  onClick={() => setLoginWithin(o.v)}
                  className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                    loginWithin === o.v ? "bg-violet-600 border-violet-600 text-white" : "bg-white border-zinc-200 text-zinc-700 hover:border-zinc-300"
                  }`}
                  data-testid={`crm-login-within-${o.v || "any"}`}
                >
                  {o.l}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Engagement chips (multi-select) — kept on a single scrollable row */}
      <div className="flex flex-nowrap overflow-x-auto gap-2 mb-4 pb-1" data-testid="crm-engagement-chips">
        {[
          { key: "recently_active", label: "Active", icon: Clock },
          { key: "has_coupons", label: "Coupons", icon: Ticket },
          { key: "has_contacts", label: "Imported", icon: UserPlus },
          { key: "opened_email", label: "Opened", icon: MailOpen },
          { key: "not_opened_email", label: "Cold", icon: Mail },
          { key: "bookings", label: "Bookings", icon: CalendarCheck },
        ].map((c) => {
          const on = activeFlags.has(c.key);
          const Icon = c.icon;
          return (
            <button
              key={c.key}
              type="button"
              onClick={() => toggleFlag(c.key)}
              aria-pressed={on}
              data-testid={`crm-chip-${c.key}`}
              className={`shrink-0 inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-medium transition-colors ${
                on
                  ? "border-violet-600 bg-violet-600 text-white"
                  : "border-zinc-300 bg-white text-zinc-600 hover:border-violet-400 hover:text-violet-700"
              }`}
            >
              <Icon size={14} />
              {c.label}
            </button>
          );
        })}
      </div>

      {/* Email campaign filter: pick a campaign, then Opened / Unopened */}
      {emailTypeOptions.length > 0 && (
        <div className="flex items-center gap-2 mb-4" data-testid="crm-campaign-filter">
          <Select
            value={emailType || "__none__"}
            onValueChange={(v) => setEmailType(v === "__none__" ? "" : v)}
          >
            <SelectTrigger
              className="h-11 rounded-xl bg-white flex-1 min-w-0"
              data-testid="crm-campaign-select"
            >
              <div className="flex items-center gap-1.5 min-w-0">
                <Mail size={14} className="text-zinc-400 shrink-0" />
                <SelectValue placeholder="Email campaign…" />
              </div>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">All campaigns</SelectItem>
              {emailTypeOptions.map((c) => (
                <SelectItem key={c.name} value={c.name}>
                  <span className="flex flex-col">
                    <span className="truncate">{c.name}</span>
                    {c.sent != null && (
                      <span className="text-[11px] text-zinc-400">
                        {c.sent} sent · {c.opened} opened · {c.open_rate}%
                      </span>
                    )}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {emailType && (
            <div className="inline-flex rounded-xl border border-zinc-300 overflow-hidden shrink-0">
              <button
                type="button"
                onClick={() => setEmailOpened("Y")}
                aria-pressed={emailOpened === "Y"}
                data-testid="crm-campaign-opened"
                className={`px-3 py-2.5 text-sm font-medium transition-colors ${
                  emailOpened === "Y"
                    ? "bg-violet-600 text-white"
                    : "bg-white text-zinc-600 hover:text-violet-700"
                }`}
              >
                Opened
              </button>
              <button
                type="button"
                onClick={() => setEmailOpened("N")}
                aria-pressed={emailOpened === "N"}
                data-testid="crm-campaign-unopened"
                className={`px-3 py-2.5 text-sm font-medium border-l border-zinc-300 transition-colors ${
                  emailOpened === "N"
                    ? "bg-violet-600 text-white"
                    : "bg-white text-zinc-600 hover:text-violet-700"
                }`}
              >
                Unopened
              </button>
            </div>
          )}
        </div>
      )}

      {loading ? (
        <SkeletonList />
      ) : error ? (
        <div
          className="mt-6 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700"
          data-testid="crm-error"
        >
          {error}
        </div>
      ) : visibleUsers.length === 0 ? (
        <div
          className="mt-10 text-center text-zinc-500 text-sm"
          data-testid="crm-empty"
        >
          {pendingCartFilter
            ? "No users have a pending cart right now."
            : "No users match your filters."}
        </div>
      ) : (
        <>
          {/* Select-all row */}
          <div className="flex items-center justify-between px-1 py-2 text-sm">
            <label className="flex items-center gap-2 text-zinc-700">
              <Checkbox
                checked={allVisibleSelected}
                onCheckedChange={toggleAllVisible}
                className="h-5 w-5 rounded-full border-2 border-zinc-300 data-[state=checked]:border-violet-600"
                data-testid="crm-select-all"
              />
              <span>Select all ({visibleUsers.length})</span>
            </label>
            {selectedCount > 0 && (
              <button
                onClick={clearSelection}
                className="text-xs text-violet-700 font-medium"
                data-testid="crm-clear-selection"
              >
                Clear
              </button>
            )}
          </div>
          {/* Column headers (sm+ only — matches the chatbot CRM table) */}
          <div className="hidden sm:flex items-center gap-2 px-3 py-2 text-[10px] uppercase tracking-widest text-zinc-500 font-semibold border-b border-zinc-200">
            <div className="w-5" />
            <div className="w-9" />
            <div className="flex-1">Customer</div>
            <div className="w-20 text-center">Last order</div>
            <div className="w-10 text-center">YTD #</div>
            <div className="w-24 text-right">YTD $</div>
            <div className="w-9" />
            <div className="w-[68px]" />
          </div>
          <ul className="space-y-2 pt-2 pb-32" data-testid="crm-user-list">
            {visibleUsers.map((u) => (
              <DirectoryRow
                key={u.id}
                user={u}
                sort={sort}
                selected={selected.has(u.id)}
                pendingCartCount={(pendingCartByUser[u.id] || []).length}
                onToggle={() => toggleOne(u.id)}
                onOpen={() => setEditId(u.id)}
                onSwitchUser={() => handleImpersonate(u)}
                onAddOrder={() => handleAddOrder(u)}
              />
            ))}
            {hasMore && !pendingCartFilter && (
              <li>
                <Button
                  variant="outline"
                  onClick={loadMore}
                  disabled={loadingMore}
                  className="w-full h-11 rounded-xl border-zinc-300"
                  data-testid="crm-load-more"
                >
                  {loadingMore ? (
                    <Loader2 className="animate-spin mr-2" size={14} />
                  ) : null}
                  Load more ({users.length} of {total.toLocaleString()})
                </Button>
              </li>
            )}
          </ul>
        </>
      )}

      {/* Bottom selection toolbar */}
      {selectedCount > 0 && (
        <div
          className="fixed bottom-20 inset-x-0 z-20 px-3 pointer-events-none"
          data-testid="crm-toolbar"
        >
          <div className="max-w-2xl mx-auto pointer-events-auto">
            <div className="rounded-2xl bg-zinc-900 text-white shadow-lg px-3 py-2.5 flex items-center gap-1 sm:gap-2 overflow-x-auto [scrollbar-width:none]">
              <div className="text-sm font-medium pl-1 pr-2 whitespace-nowrap shrink-0">
                {selectedCount} selected
              </div>
              <div className="flex-1" />
              <ToolbarBtn
                icon={Ticket}
                label="Coupon"
                onClick={() => {
                  if (selectedCount === 0) return;
                  setCouponModalOpen(true);
                }}
                testId="crm-toolbar-coupon"
              />
              <ToolbarBtn
                icon={Gift}
                label="Points"
                onClick={() => {
                  if (selectedCount === 0) return;
                  setPointsModalOpen(true);
                }}
                testId="crm-toolbar-points"
              />
              <ToolbarBtn
                icon={Mail}
                label="Email"
                onClick={() => {
                  const withEmail = selectedUsers.filter((u) => u.email);
                  if (withEmail.length === 0) {
                    toast.error("None of the selected users have an email");
                    return;
                  }
                  if (withEmail.length < selectedUsers.length) {
                    toast.message(
                      `${selectedUsers.length - withEmail.length} user(s) skipped — no email on file`
                    );
                  }
                  setEmailModalOpen(true);
                }}
                testId="crm-toolbar-email"
              />
              <ToolbarBtn
                icon={MessageSquare}
                label="SMS"
                onClick={() => {
                  const withPhone = selectedUsers.filter((u) => u.phone);
                  if (withPhone.length === 0) {
                    toast.error("None of the selected users have a phone number");
                    return;
                  }
                  if (withPhone.length < selectedUsers.length) {
                    toast.message(
                      `${selectedUsers.length - withPhone.length} user(s) skipped — no phone on file`
                    );
                  }
                  setSmsModalOpen(true);
                }}
                testId="crm-toolbar-sms"
              />
              <ToolbarBtn
                icon={Trash2}
                label={deletingUsers ? "Deleting…" : "Delete"}
                danger
                onClick={deleteSelectedUsers}
                testId="crm-toolbar-delete"
              />
              {pendingCartFilter && (
                <ToolbarBtn
                  icon={ShoppingCart}
                  label={deletingCarts ? "Deleting…" : "Delete cart"}
                  danger
                  onClick={deleteSelectedCarts}
                  testId="crm-toolbar-delete-cart"
                />
              )}
            </div>
          </div>
        </div>
      )}

      <EmailSendModal
        open={emailModalOpen}
        onOpenChange={setEmailModalOpen}
        recipients={selectedUsers
          .filter((u) => u.email)
          .map((u) => ({
            id: u.id,
            email: u.email,
            first_name: u.first_name,
            last_name: u.last_name,
            full_name: u.full_name,
          }))}
      />

      <SmsSendModal
        open={smsModalOpen}
        onOpenChange={setSmsModalOpen}
        recipients={selectedUsers.map((u) => ({
          id: u.id,
          phone: u.phone,
          first_name: u.first_name,
          last_name: u.last_name,
        }))}
      />

      <CouponUpgradeModal
        open={couponModalOpen}
        onOpenChange={setCouponModalOpen}
        recipients={selectedUsers.map((u) => ({
          id: u.id,
          has_account: true,
          first_name: u.first_name,
          full_name: u.full_name,
          role_name: u.role_name,
        }))}
        onApplied={() => loadDirectory()}
      />

      <PointsAwardModal
        open={pointsModalOpen}
        onOpenChange={setPointsModalOpen}
        recipients={selectedUsers.map((u) => ({
          id: u.id,
          first_name: u.first_name,
          full_name: u.full_name,
        }))}
      />

      <AdminUserEditSheet
        open={!!editId}
        onOpenChange={(o) => !o && setEditId(null)}
        callerUserRecordId={callerRecId}
        userRecordId={editId}
        pendingCartItems={editId ? pendingCartByUser[editId] || [] : []}
        hasPendingCart={editId ? pendingCartIds.has(editId) : false}
        onSaved={(p) => {
          if (!p) return;
          setUsers((list) =>
            list.map((u) =>
              u.id === p.id
                ? {
                    ...u,
                    commission_perc: p.commission_perc,
                    tax_exempted: p.tax_exempted,
                    approved: p.approved,
                    role_name: p.role_name || u.role_name,
                    first_name: p.first_name || u.first_name,
                    last_name: p.last_name || u.last_name,
                    full_name:
                      `${p.first_name || ""} ${p.last_name || ""}`.trim() ||
                      u.full_name,
                  }
                : u
            )
          );
        }}
      />

      <DraftOrderSheet
        open={!!draftCustomer}
        onOpenChange={(o) => !o && setDraftCustomer(null)}
        customer={draftCustomer}
        callerUserRecordId={callerRecId}
        onCreated={() => {
          reqIdRef.current += 1;
        }}
      />
    </div>
  );
}

function DirectoryRow({
  user,
  sort,
  selected,
  pendingCartCount = 0,
  onToggle,
  onOpen,
  onSwitchUser,
  onAddOrder,
}) {
  const name = user.full_name || user.email || "Unnamed";
  const initials = name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join("");

  // Always show YTD figures (matches the chatbot view). Backend supplies
  // `ytd_num_orders` + `ytd_gross_revenue` when the directory sort needs
  // order aggregates. Fall back to all-time when YTD isn't present.
  const numOrders = user.ytd_num_orders ?? user.num_orders ?? 0;
  const revenue = user.ytd_gross_revenue ?? user.gross_revenue ?? 0;
  const lastDate = user.last_order_date || "";

  // Format the date the chatbot way: M/D/YYYY (US locale)
  let lastDateStr = "—";
  if (lastDate) {
    try {
      const d = new Date(lastDate);
      if (!isNaN(d)) lastDateStr = d.toLocaleDateString("en-US");
      else lastDateStr = lastDate;
    } catch {
      lastDateStr = lastDate;
    }
  }

  // Suppress the unused 'sort' lint when not needed
  void sort;

  // Last login date (Airtable login_time) so admins can gauge activity.
  let lastLoginStr = "";
  if (user.last_login) {
    try {
      const d = new Date(user.last_login);
      lastLoginStr = isNaN(d) ? "" : d.toLocaleDateString("en-US");
    } catch {
      lastLoginStr = "";
    }
  }

  return (
    <li
      className={`rounded-xl bg-white border transition-all ${
        selected
          ? "border-violet-400 ring-1 ring-violet-300/60"
          : "border-zinc-200 hover:border-violet-300 hover:shadow-sm"
      }`}
      data-testid={`crm-row-${user.id}`}
    >
      <div className="flex items-center gap-2 p-3">
        <Checkbox
          checked={selected}
          onCheckedChange={onToggle}
          onClick={(e) => e.stopPropagation()}
          className="h-5 w-5 rounded-full border-2 border-zinc-300 data-[state=checked]:border-violet-600"
          data-testid={`crm-checkbox-${user.id}`}
        />
        {user.image_url ? (
          <img
            src={user.image_url}
            alt={name}
            className="w-9 h-9 rounded-full object-cover shrink-0"
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
        ) : (
          <div className="w-9 h-9 rounded-full bg-violet-100 text-violet-700 flex items-center justify-center text-xs font-semibold shrink-0">
            {initials || "?"}
          </div>
        )}

        {/* Name + company — clickable, blue underline (chatbot style) */}
        <button
          type="button"
          onClick={onOpen}
          className="min-w-0 flex-1 text-left"
          data-testid={`crm-row-open-${user.id}`}
        >
          <div className="flex items-center gap-1.5">
            <span className="text-sm font-semibold text-violet-700 underline truncate">
              {name}
            </span>
            {pendingCartCount > 0 && (
              <span
                className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-amber-700 bg-amber-100 rounded-full px-1.5 py-0.5 shrink-0"
                title={`${pendingCartCount} item${pendingCartCount === 1 ? "" : "s"} in pending cart`}
                data-testid={`crm-row-cart-badge-${user.id}`}
              >
                <ShoppingCart size={9} />
                {pendingCartCount}
              </span>
            )}
          </div>
          {user.company_name && (
            <div className="text-[11px] italic text-zinc-500 truncate">
              {user.company_name}
            </div>
          )}
          {lastLoginStr && (
            <div
              className="text-[10px] text-zinc-400 truncate mt-0.5 inline-flex items-center gap-1"
              data-testid={`crm-row-lastlogin-${user.id}`}
            >
              <Clock size={9} className="shrink-0" />
              Last login {lastLoginStr}
            </div>
          )}
          {user.emailed && (
            <div
              className={`text-[10px] truncate mt-0.5 flex items-center gap-1 ${
                user.opened_email ? "text-emerald-600" : "text-zinc-400"
              }`}
              data-testid={`crm-row-email-${user.id}`}
            >
              {user.opened_email ? (
                <MailOpen size={9} className="shrink-0" />
              ) : (
                <Mail size={9} className="shrink-0" />
              )}
              {user.last_email_type || "Email"} · {user.opened_email ? `Opened (${user.opened_count})` : "Not opened"}
            </div>
          )}
          {(user.mindful_minerals_revenue > 0 || user.orevital_revenue > 0) && (
            <div
              className="text-[10px] text-zinc-500 truncate mt-0.5 flex items-center gap-2"
              data-testid={`crm-row-brandrev-${user.id}`}
            >
              <span className="font-medium text-emerald-700">
                MI ${Number(user.mindful_minerals_revenue || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
              <span className="text-zinc-300">·</span>
              <span className="font-medium text-indigo-700">
                OV ${Number(user.orevital_revenue || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            </div>
          )}
          <div className="text-[10px] text-zinc-400 truncate sm:hidden mt-0.5">
            {lastDateStr} · {numOrders} · ${Number(revenue).toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </div>
        </button>

        {/* Numeric stats — visible on sm+ */}
        <div className="hidden sm:flex items-center gap-4 text-sm text-zinc-700 shrink-0">
          <div className="w-20 text-center text-zinc-600" title="Last order">
            {lastDateStr}
          </div>
          <div className="w-10 text-center font-medium" title="Orders YTD">
            {numOrders}
          </div>
          <div className="w-24 text-right font-medium" title="Revenue YTD">
            ${Number(revenue).toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </div>
        </div>

        {/* Switch user */}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onSwitchUser?.();
          }}
          className="h-9 w-9 rounded-lg bg-violet-600 hover:bg-violet-700 text-white flex items-center justify-center shrink-0"
          title="Sign in as this user"
          aria-label="Sign in as this user"
          data-testid={`crm-switch-user-${user.id}`}
        >
          <UserCog size={14} />
        </button>

        {/* + Order */}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onAddOrder?.();
          }}
          className="h-9 px-3 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-semibold flex items-center gap-1 shrink-0"
          title="Create a new order for this user"
          data-testid={`crm-add-order-${user.id}`}
        >
          <Plus size={12} />
          Order
        </button>
      </div>
    </li>
  );
}

function SkeletonList() {
  return (
    <ul className="space-y-2" data-testid="crm-skeleton">
      {Array.from({ length: 8 }).map((_, i) => (
        <li
          key={i}
          className="rounded-xl bg-white border border-zinc-200 p-3 flex items-center gap-3"
        >
          <div className="w-10 h-10 rounded-full bg-zinc-100 animate-pulse" />
          <div className="flex-1 space-y-1.5">
            <div className="h-3 bg-zinc-100 rounded animate-pulse w-1/2" />
            <div className="h-3 bg-zinc-100 rounded animate-pulse w-2/3" />
          </div>
          <div className="h-5 w-16 bg-zinc-100 rounded animate-pulse" />
        </li>
      ))}
    </ul>
  );
}

/* ============================================================== */
/* Dealer: My Team (existing behavior)                            */
/* ============================================================== */
function DealerContacts({ user }) {
  const callerRecId = user?.user_record_id || user?.record_id;
  const [crmView, setCrmView] = useState("contacts"); // "contacts" | "bookings"
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [smsModalOpen, setSmsModalOpen] = useState(false);
  const [couponModalOpen, setCouponModalOpen] = useState(false);
  // "Send to CRM" from the Video Studio: /app/contacts?video=<id> attaches the video to Email/SMS sends.
  const [attachedVideo, setAttachedVideo] = useState(null);
  useEffect(() => {
    const vid = new URLSearchParams(window.location.search).get("video");
    if (!vid || !callerRecId) return;
    promoShare(callerRecId, vid).then((s) => setAttachedVideo({ id: vid, ...s })).catch(() => toast.error("That video is not ready to share"));
  }, [callerRecId]);
  const clearVideo = () => { setAttachedVideo(null); window.history.replaceState(null, "", "/app/contacts"); };
  const [addOpen, setAddOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  // Detail/edit sheet for a single contact (tap a row to open).
  const [detailId, setDetailId] = useState(null);
  // Draft-order sheet — approved dealers can place an order for a contact.
  const [draftCustomer, setDraftCustomer] = useState(null);

  const loadTeam = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchMyTeam(user);
      setContacts(Array.isArray(data?.team_members) ? data.team_members : []);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Failed to load contacts");
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        if (!active) return;
        await loadTeam();
      } catch {
        /* swallow */
      }
    })();
    return () => {
      active = false;
    };
  }, [loadTeam]);

  const [filters, setFilters] = useState(EMPTY_DEALER_FILTERS);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const base = applyDealerContactFilters(contacts, filters);
    if (!q) return base;
    return base.filter((c) =>
      [c.full_name, c.first_name, c.last_name, c.email, c.phone]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  }, [contacts, query, filters]);

  const allVisibleSelected =
    filtered.length > 0 && filtered.every((c) => selected.has(c.id));

  function toggleOne(id) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleAllVisible() {
    setSelected((prev) => {
      const next = new Set(prev);
      const allOn = filtered.every((c) => next.has(c.id));
      if (allOn) filtered.forEach((c) => next.delete(c.id));
      else filtered.forEach((c) => next.add(c.id));
      return next;
    });
  }

  function clearSelection() {
    setSelected(new Set());
  }

  const selectedCount = selected.size;
  const selectedContacts = useMemo(
    () => contacts.filter((c) => selected.has(c.id)),
    [contacts, selected]
  );
  const [deleting, setDeleting] = useState(false);
  async function handleDeleteSelected() {
    if (selectedContacts.length === 0) return;
    const ok = window.confirm(
      `Remove ${selectedCount} contact${selectedCount === 1 ? "" : "s"} from your CRM? This won't delete their account.`
    );
    if (!ok) return;
    setDeleting(true);
    try {
      const res = await deleteContacts({
        callerUserRecordId: callerRecId,
        memberIds: selectedContacts.map((c) => c.id),
      });
      toast.success(
        `${res.deleted || 0} contact${(res.deleted || 0) === 1 ? "" : "s"} removed`
      );
      clearSelection();
      await loadTeam();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Delete failed");
    } finally {
      setDeleting(false);
    }
  }
  const placeholderAction = (label) => () =>
    toast(`${label} — coming next`, {
      description: `${selectedCount} contact${selectedCount === 1 ? "" : "s"} selected`,
    });

  return (
    <div className="px-4 pt-4" data-testid="contacts-page">
      {attachedVideo && (
        <div className="mb-3 rounded-2xl border border-amber-200 bg-amber-50 p-3 flex items-center gap-3" data-testid="crm-attached-video">
          {attachedVideo.thumbnail_url && <img src={attachedVideo.thumbnail_url} alt="" className="h-14 w-8 rounded-md object-cover bg-zinc-900 shrink-0" />}
          <div className="flex-1 min-w-0">
            <div className="text-xs font-bold uppercase tracking-wide text-amber-800">Video attached</div>
            <div className="text-sm font-medium text-zinc-900 truncate">{attachedVideo.subject}</div>
            <div className="text-xs text-amber-800/80">Select the contacts below, then tap <b>Email</b> or <b>SMS</b> — the video link is pre-filled.</div>
          </div>
          <button type="button" onClick={clearVideo} className="h-8 w-8 rounded-full hover:bg-amber-100 inline-flex items-center justify-center text-amber-900" data-testid="crm-attached-video-clear"><XIcon size={14} /></button>
        </div>
      )}
      <div className="flex items-center justify-between mb-3">
        <h1 className="text-2xl font-semibold tracking-tight text-zinc-900">
          CRM
        </h1>
        <div className="text-xs text-zinc-500" data-testid="contacts-count">
          {crmView === "bookings" ? "Bookings" : `${contacts.length} total`}
        </div>
      </div>

      <div className="flex gap-1 bg-zinc-100 rounded-xl p-1 mb-4 w-fit" data-testid="crm-view-toggle">
        {[
          { k: "contacts", label: "CRM" },
          { k: "bookings", label: "Bookings" },
        ].map((v) => (
          <button
            key={v.k}
            onClick={() => setCrmView(v.k)}
            className={`px-4 py-1.5 rounded-lg text-sm font-semibold transition-colors ${crmView === v.k ? "bg-white shadow-sm text-violet-700" : "text-zinc-500"}`}
            data-testid={`crm-view-${v.k}`}
          >
            {v.label}
          </button>
        ))}
      </div>

      {crmView === "bookings" && <BookingsList rec={callerRecId} />}

      {crmView === "contacts" && (<>
      <div className="flex items-center gap-2 mb-3">
        <div className="relative flex-1">
          <Search
            size={18}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400"
          />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search contacts"
            className="pl-10 h-11 rounded-xl border-zinc-200 bg-white focus-visible:ring-violet-500"
            data-testid="contacts-search-input"
          />
        </div>
        <Button
          variant="outline"
          size="icon"
          className="h-11 w-11 rounded-xl border-zinc-200"
          onClick={() => setImportOpen(true)}
          aria-label="Import contacts"
          data-testid="contacts-import-btn"
        >
          <Upload size={18} />
        </Button>
        <Button
          size="icon"
          className="h-11 w-11 rounded-xl bg-violet-600 hover:bg-violet-700 text-white"
          onClick={() => setAddOpen(true)}
          aria-label="Add contact"
          data-testid="contacts-add-btn"
        >
          <Plus size={20} />
        </Button>
      </div>

      <DealerContactFilters value={filters} onChange={setFilters} />

      {filtered.length > 0 && (
        <div className="flex items-center justify-between px-1 py-2 text-sm">
          <label className="flex items-center gap-2 text-zinc-700">
            <Checkbox
              checked={allVisibleSelected}
              onCheckedChange={toggleAllVisible}
              className="h-5 w-5 rounded-full border-2 border-zinc-300 data-[state=checked]:border-violet-600"
              data-testid="contacts-select-all"
            />
            <span>Select all ({filtered.length})</span>
          </label>
          {selectedCount > 0 && (
            <button
              onClick={clearSelection}
              className="text-xs text-violet-700 font-medium"
              data-testid="contacts-clear-selection"
            >
              Clear
            </button>
          )}
        </div>
      )}

      {loading ? (
        <div className="py-20 flex flex-col items-center text-zinc-500">
          <Loader2 className="animate-spin mb-2" />
          <span className="text-sm">Loading your contacts…</span>
        </div>
      ) : error ? (
        <div
          className="mt-6 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700"
          data-testid="contacts-error"
        >
          {error}
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState hasContacts={contacts.length > 0} query={query} />
      ) : (
        <ul className="space-y-2 pb-32" data-testid="contacts-list">
          {filtered.map((c) => (
            <ContactRow
              key={c.id}
              contact={c}
              selected={selected.has(c.id)}
              onToggle={() => toggleOne(c.id)}
              onOpen={() => setDetailId(c.id)}
              onAddOrder={() => {
                const buyerId = c.contact_record_id;
                if (!buyerId) {
                  toast.error(
                    "This contact has no linked account yet — add them as a customer first."
                  );
                  return;
                }
                setDraftCustomer({ ...c, id: buyerId });
              }}
            />
          ))}
        </ul>
      )}

      {selectedCount > 0 && (
        <div
          className="fixed bottom-20 inset-x-0 z-20 px-3 pointer-events-none"
          data-testid="contacts-toolbar"
        >
          <div className="max-w-2xl mx-auto pointer-events-auto">
            <div className="rounded-2xl bg-zinc-900 text-white shadow-lg px-3 py-2.5 flex items-center gap-2">
              <div className="text-sm font-medium pl-1 pr-2">
                {selectedCount} selected
              </div>
              <div className="flex-1" />
              <ToolbarBtn
                icon={Ticket}
                label="Coupon"
                onClick={() => {
                  if (selectedContacts.length === 0) return;
                  setCouponModalOpen(true);
                }}
                testId="toolbar-apply-coupon"
              />
              <ToolbarBtn
                icon={Mail}
                label="Email"
                onClick={() => {
                  const withEmail = selectedContacts.filter((c) => c.email);
                  if (withEmail.length === 0) {
                    toast.error("None of the selected contacts have an email");
                    return;
                  }
                  if (withEmail.length < selectedContacts.length) {
                    toast.message(
                      `${selectedContacts.length - withEmail.length} contact(s) skipped — no email on file`
                    );
                  }
                  setEmailModalOpen(true);
                }}
                testId="toolbar-send-email"
              />
              <ToolbarBtn
                icon={MessageSquare}
                label="SMS"
                onClick={() => {
                  const withPhone = selectedContacts.filter((c) => c.phone);
                  if (withPhone.length === 0) {
                    toast.error("None of the selected contacts have a phone number");
                    return;
                  }
                  if (withPhone.length < selectedContacts.length) {
                    toast.message(
                      `${selectedContacts.length - withPhone.length} contact(s) skipped — no phone on file`
                    );
                  }
                  setSmsModalOpen(true);
                }}
                testId="toolbar-send-sms"
              />
              <ToolbarBtn
                icon={Trash2}
                label="Delete"
                danger
                onClick={handleDeleteSelected}
                disabled={deleting}
                testId="toolbar-delete"
              />
            </div>
          </div>
        </div>
      )}

      </>)}

      <EmailSendModal
        open={emailModalOpen}
        onOpenChange={setEmailModalOpen}
        recipients={selectedContacts.filter((c) => c.email)}
        preset={attachedVideo ? { subject: attachedVideo.subject, body: attachedVideo.html } : null}
      />

      <SmsSendModal
        open={smsModalOpen}
        onOpenChange={setSmsModalOpen}
        preset={attachedVideo ? { message: attachedVideo.sms, linkUrl: attachedVideo.url, imageUrl: attachedVideo.thumbnail_url } : null}
        recipients={selectedContacts.map((c) => ({
          id: c.id,
          phone: c.phone,
          first_name: c.first_name,
          last_name: c.last_name,
        }))}
      />

      <CouponUpgradeModal
        open={couponModalOpen}
        onOpenChange={setCouponModalOpen}
        recipients={selectedContacts.map((c) => ({
          id: c.contact_record_id || c.id,
          has_account: !!c.contact_record_id,
          first_name: c.first_name,
          full_name: c.full_name,
          role_name: c.role,
        }))}
        onApplied={() => loadTeam()}
      />

      <AddContactDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        userRecordId={callerRecId}
        onAdded={() => loadTeam()}
      />

      <ImportContactsDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        userRecordId={callerRecId}
        onCompleted={() => loadTeam()}
      />

      <ContactDetailSheet
        open={!!detailId}
        onOpenChange={(v) => {
          if (!v) setDetailId(null);
        }}
        contactRowId={detailId}
        callerUserRecordId={callerRecId}
        onSaved={() => loadTeam()}
        onAddOrder={() => {
          const c = contacts.find((x) => x.id === detailId);
          const buyerId = c?.contact_record_id;
          if (!buyerId) {
            toast.error("This contact has no linked account yet — add them as a customer first.");
            return;
          }
          setDetailId(null);
          setDraftCustomer({ ...c, id: buyerId });
        }}
      />

      <DraftOrderSheet
        open={!!draftCustomer}
        onOpenChange={(o) => !o && setDraftCustomer(null)}
        customer={draftCustomer}
        callerUserRecordId={callerRecId}
        dealerMode
        onCreated={() => {
          setDraftCustomer(null);
          loadTeam();
        }}
      />
    </div>
  );
}

function ContactRow({ contact, selected, onToggle, onOpen, onAddOrder }) {
  const name =
    contact.full_name || `${contact.first_name || ""} ${contact.last_name || ""}`.trim() ||
    contact.email ||
    "Unnamed contact";
  const initials = name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join("");

  return (
    <li
      onClick={onOpen}
      className={`rounded-xl bg-white border transition-all cursor-pointer ${
        selected
          ? "border-violet-400 ring-1 ring-violet-300/60"
          : "border-zinc-200 hover:border-zinc-300"
      }`}
      data-testid={`contact-row-${contact.id}`}
    >
      <div className="flex items-center gap-3 p-3">
        <div onClick={(e) => e.stopPropagation()}>
          <Checkbox
            checked={selected}
            onCheckedChange={onToggle}
            className="h-5 w-5 rounded-full border-2 border-zinc-300 data-[state=checked]:border-violet-600"
            data-testid={`contact-checkbox-${contact.id}`}
          />
        </div>
        <div className="w-10 h-10 rounded-full bg-violet-100 text-violet-700 flex items-center justify-center text-sm font-semibold shrink-0">
          {initials || "?"}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <div className="font-medium text-zinc-900 truncate">{name}</div>
          </div>
          <div className="text-xs text-zinc-500 truncate">
            {contact.email || contact.phone || "—"}
          </div>
          {contact.order_count > 0 && (
            <div className="mt-0.5 flex items-center gap-2 text-[11px]" data-testid={`contact-orders-${contact.id}`}>
              <span className="font-semibold text-emerald-700">
                ${Number(contact.revenue || 0).toFixed(2)}
              </span>
              <span className="text-zinc-400">·</span>
              <span className="text-zinc-500">
                {contact.order_count} order{contact.order_count === 1 ? "" : "s"}
              </span>
              {contact.last_order_total != null && (
                <span className="text-zinc-400 truncate">
                  · last ${Number(contact.last_order_total).toFixed(2)}
                  {contact.last_order_date ? ` (${contact.last_order_date})` : ""}
                </span>
              )}
            </div>
          )}
        </div>
        <Badge
          className={`shrink-0 capitalize font-medium border-0 ${roleClass(contact.role)}`}
        >
          {contact.role || "—"}
        </Badge>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onAddOrder?.();
          }}
          className="shrink-0 w-9 h-9 rounded-full bg-emerald-50 text-emerald-600 hover:bg-emerald-100 flex items-center justify-center transition-colors"
          data-testid={`contact-add-order-${contact.id}`}
          aria-label={`New invoice for ${name}`}
          title="New invoice"
        >
          <ShoppingBag size={15} />
        </button>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onOpen();
          }}
          className="shrink-0 w-9 h-9 rounded-full bg-violet-50 text-violet-600 hover:bg-violet-100 flex items-center justify-center transition-colors"
          data-testid={`contact-edit-${contact.id}`}
          aria-label={`Edit ${name}`}
        >
          <Pencil size={15} />
        </button>
      </div>
    </li>
  );
}

function ToolbarBtn({ icon: Icon, label, onClick, danger, testId, disabled }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      className={`flex flex-col items-center justify-center px-2 sm:px-2.5 py-1 rounded-lg text-[10px] font-medium whitespace-nowrap shrink-0 transition-colors disabled:opacity-50 ${
        danger
          ? "text-rose-300 hover:bg-rose-500/15"
          : "text-zinc-200 hover:bg-white/10"
      }`}
    >
      <Icon size={18} className="mb-0.5" />
      {label}
    </button>
  );
}

function EmptyState({ hasContacts, query }) {
  if (hasContacts && query) {
    return (
      <div className="py-20 text-center text-zinc-500" data-testid="contacts-empty-search">
        <p className="text-sm">No contacts match &ldquo;{query}&rdquo;.</p>
      </div>
    );
  }
  return (
    <div
      className="mt-8 rounded-2xl border border-dashed border-zinc-300 bg-white p-8 text-center"
      data-testid="contacts-empty-state"
    >
      <div className="w-12 h-12 rounded-2xl bg-violet-100 mx-auto flex items-center justify-center mb-3">
        <UsersIcon className="text-violet-600" size={22} />
      </div>
      <div className="font-semibold text-zinc-900 mb-1">No contacts yet</div>
      <p className="text-sm text-zinc-500 max-w-xs mx-auto">
        Tap the <span className="font-medium text-zinc-700">+</span> button to add
        your first contact, or <span className="font-medium text-zinc-700">Upload</span>{" "}
        a CSV to import in bulk.
      </p>
    </div>
  );
}
